"use client";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { ChevronDown, Search } from "lucide-react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { useI18N, useAccessibilityTags, useAuth, useSavedFilters, useZoomDetection } from "common/hooks";
import {
  buildTagSections,
  isInstanceSelected,
  isTagSelectedOutside,
  mergeTagSections,
  removeTagExcept,
  subtractSelections,
  toggleInstance,
  toggleSectionOnly,
  unionSelections,
  withFamilySections,
  type SelectionKey,
} from "common/utils/accessibilitySelection";
import { Accordion, AccordionItem, AccordionContent } from "@/shadcn/components/ui/accordion";
import { cn } from "@/lib/utils";
import Checkbox from "@/components/common/Checkbox";
import { StandardButton } from "@/components/common/StandardButton";
import Switch from "@/components/common/Switch";
import { useAtom } from "jotai";
import { savedFiltersAppliedAtom } from "@/store/atoms";
import { splitStepFreeAlternatives } from "@/lib/accessibilityFilterState";
import { hasKeyboardFocus } from "@/lib/focusModality";
import FocusTrap from "focus-trap-react";
import ScrollContainer from "react-indiana-drag-scroll";

// ============================================================================
// Types
// ============================================================================

/** One filter section as the search response lists it (`accessibility_tags` on the search API). */
interface FilterSection {
  id: number;
  name: string;
  /** "complex" = a merged sub-heading of complex venues; absent on the global catalogue. */
  kind?: "basic" | "complex";
  accessibility_tags: { id: number; name: string }[];
}

/** The panel's widest layout (`lg:w-[520px]`); a fixed-position host clamps its anchor with it. */
export const ACCESSIBILITY_FILTER_PANEL_MAX_WIDTH = 520;

export interface AccessibilityFilterPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Callback when panel should close */
  onClose: () => void;
  /** Id of the dialog element, for a trigger's `aria-controls` */
  id?: string;
  /** Whether the "Apply saved accessibility filters" switch is offered (default true) */
  showSavedFilters?: boolean;
  /** Move focus into the panel when it opens: the search box if opened from the keyboard, else the panel itself */
  focusOnOpen?: boolean;
  /** Selected keys: `"57:428"` = tag in section, `"428"` = tag in any section (see common/utils/accessibilitySelection) */
  selectedOptions: SelectionKey[];
  /** Callback when the selection changes */
  onOptionsChange: (keys: SelectionKey[]) => void;
  /** Callback when clear all is clicked */
  onClearAll: () => void;
  /** Runs the host's search; when set, "Clear all" and "Apply filters" sit in a footer instead of the header */
  onApply?: () => void;
  /** Custom className for positioning */
  className?: string;
  /** Optional external accessibility tags data (overrides hook data if provided) */
  accessibilityTagsData?: any[];
}

// ============================================================================
// Component
// ============================================================================

export function AccessibilityFilterPanel({
  isOpen,
  onClose,
  id,
  showSavedFilters = true,
  focusOnOpen = false,
  selectedOptions,
  onOptionsChange,
  onClearAll,
  onApply,
  className,
  accessibilityTagsData,
}: AccessibilityFilterPanelProps) {
  const i18n = useI18N();
  const hookData = useAccessibilityTags();
  const { isLoggedIn } = useAuth();
  const { savedFilterKeys, loadSavedFilters } = useSavedFilters();
  const [isSavedFiltersApplied, setIsSavedFiltersApplied] = useAtom(
    savedFiltersAppliedAtom
  );

  // Use external data if provided, otherwise use hook data
  const accessibilityTags =
    accessibilityTagsData !== undefined
      ? accessibilityTagsData
      : hookData.accessibilityTags;

  // Prepare data manually if using external data (wrapped in useMemo to avoid recreating on every render)
  const { preparedData, tagsToAdd } = useMemo(() => {
    if (accessibilityTagsData === undefined) {
      return {
        preparedData: hookData.preparedData,
        tagsToAdd: hookData.tagsToAdd,
      };
    }

    return splitStepFreeAlternatives(accessibilityTags);
  }, [
    accessibilityTagsData,
    accessibilityTags,
    hookData.preparedData,
    hookData.tagsToAdd,
  ]);

  // A first tick spreads over the basic sections only: no complex venue holds a feature in every
  // sub-heading. A section-less key still materialises into every visible box.
  const tagSections = useMemo(
    () => buildTagSections(preparedData.filter((section: FilterSection) => section.kind !== "complex")),
    [preparedData],
  );
  const allSections = useMemo(() => buildTagSections(preparedData), [preparedData]);
  const complexSectionIds = useMemo(
    () => preparedData.filter((section: FilterSection) => section.kind === "complex").map((section: FilterSection) => section.id),
    [preparedData],
  );
  // The step-free alternatives live under each ticked step-free instance, so their
  // sections follow the current selection rather than the catalogue.
  const familyIds = useMemo(() => tagsToAdd.map((tag: any) => tag.id as number), [tagsToAdd]);
  const familySections = useMemo(
    () => withFamilySections(tagSections, selectedOptions, 428, familyIds),
    [tagSections, selectedOptions, familyIds],
  );
  // Every visible box: an alternative shows under ticked basic step-free instances and, as an ordinary tag, in complex sections.
  const visibleSections = useMemo(() => mergeTagSections(allSections, familySections), [allSections, familySections]);

  const [searchTerm, setSearchTerm] = useState("");
  // Section state is keyed by section id: a basic and a complex section may share a name.
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  // Accordion state: every section open when it first appears; the panel unmounts on close, so nothing is remembered.
  const [openSections, setOpenSections] = useState<string[]>([]);
  const seenSectionKeys = useRef<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [liveMessage, setLiveMessage] = useState("");
  const announcedSectionCount = useRef<number | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const contentRef = useRef<HTMLDivElement>(null);
  const categoryTriggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const categoryChipRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const isProgrammaticScroll = useRef(false);

  const isZoomedIn = useZoomDetection(1.25);

  // Load saved filters when panel opens
  useEffect(() => {
    if (isOpen && isLoggedIn && showSavedFilters) {
      loadSavedFilters();
    }
  }, [isOpen, isLoggedIn, showSavedFilters, loadSavedFilters]);

  // A focused text box always shows its ring (and raises the keyboard on touch), so only a panel
  // opened from the keyboard lands in the search box; a click or tap focuses the dialog itself.
  const rendered = isOpen && !!accessibilityTags && preparedData.length > 0;
  useEffect(() => {
    if (!focusOnOpen || !rendered) return;
    const coarsePointer = typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;
    const target = !coarsePointer && hasKeyboardFocus() ? searchInputRef.current : panelRef.current;
    target?.focus({ preventScroll: true });
  }, [focusOnOpen, rendered]);

  // Set first category as selected by default when data is loaded
  useEffect(() => {
    if (preparedData.length > 0 && !selectedCategory) {
      const firstCategoryWithTags = preparedData.find(
        (category: any) => category.accessibility_tags.length > 0,
      );
      if (firstCategoryWithTags) {
        setSelectedCategory(String(firstCategoryWithTags.id));
      }
    }
  }, [preparedData, selectedCategory]);

  // Open each section the first time it appears (all expanded by default); a per-search list
  // brings new sections with each search.
  useEffect(() => {
    const fresh = (preparedData as FilterSection[])
      .filter((category) => category.accessibility_tags.length > 0)
      .map((category) => String(category.id))
      .filter((key) => !seenSectionKeys.current.has(key));
    if (fresh.length === 0) return;
    fresh.forEach((key) => seenSectionKeys.current.add(key));
    setOpenSections((prev) => [...prev, ...fresh]);
  }, [preparedData]);

  // Set last category as expanded by default when data is loaded
  useEffect(() => {
    if (preparedData.length > 0 && expandedCategories.size === 0) {
      const categoriesWithTags = preparedData.filter(
        (category: any) => category.accessibility_tags.length > 0,
      );
      if (categoriesWithTags.length > 0) {
        const lastCategory = categoriesWithTags[categoriesWithTags.length - 1];
        setExpandedCategories(new Set([String(lastCategory.id)]));
      }
    }
  }, [preparedData, expandedCategories.size]);

  // Tell screen readers when a new search changes how many sections there are.
  const sectionCount = useMemo(
    () => (preparedData as FilterSection[]).filter((category) => category.accessibility_tags.length > 0).length,
    [preparedData],
  );
  useEffect(() => {
    if (!isOpen) return;
    if (announcedSectionCount.current !== null && announcedSectionCount.current !== sectionCount) {
      setLiveMessage(i18n.t("accessibilitySectionsAvailable", { count: sectionCount }));
    }
    announcedSectionCount.current = sectionCount;
  }, [isOpen, sectionCount, i18n]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  // Scroll to category section when category chip is clicked
  const handleCategoryClick = useCallback((categoryKey: string) => {
    setSelectedCategory(categoryKey);
    // A collapsed section has nothing to scroll to: open it first.
    setOpenSections((prev) => (prev.includes(categoryKey) ? prev : [...prev, categoryKey]));
    isProgrammaticScroll.current = true;

    const sectionEl = sectionRefs.current[categoryKey];
    const triggerEl = categoryTriggerRefs.current[categoryKey];

    if (sectionEl && contentRef.current) {
      const containerTop = contentRef.current.getBoundingClientRect().top;
      const sectionTop = sectionEl.getBoundingClientRect().top;
      const offset = sectionTop - containerTop;
      contentRef.current.scrollTo({
        top: contentRef.current.scrollTop + offset - 8,
        behavior: "smooth",
      });

      // Move focus to the section header once the scroll settles
      setTimeout(() => {
        triggerEl?.focus();
        isProgrammaticScroll.current = false;
      }, 1000);
    }
  }, []);

  // Filter tags based on search term
  const filterTags = useCallback((tags: any[]) => {
    if (!searchTerm) return tags;
    return tags.filter((tag: any) =>
      tag.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm]);

  // Get filtered categories with their tags
  const filteredCategories = useMemo(() => {
    return preparedData
      .map((category: any) => ({
        ...category,
        accessibility_tags: filterTags(category.accessibility_tags),
      }))
      .filter((category: any) => category.accessibility_tags.length > 0);
  }, [preparedData, filterTags]);

  // Scroll spy - auto-select category chip when scrolling through sections
  useEffect(() => {
    if (!contentRef.current || filteredCategories.length === 0) return;

    const scrollContainer = contentRef.current;
    let timeoutId: NodeJS.Timeout;

    const handleScroll = () => {
      // Don't update during programmatic scrolls
      if (isProgrammaticScroll.current) {
        return;
      }

      // Debounce scroll updates to prevent jitter
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (!scrollContainer) return;

        const containerRect = scrollContainer.getBoundingClientRect();
        const containerTop = containerRect.top;
        const containerHeight = containerRect.height;
        
        let mostVisibleCategory = "";
        let maxVisibleArea = 0;

        // Check each category section to find which has most visible content
        filteredCategories.forEach((category: any) => {
          if (category.accessibility_tags.length <= 0) return;
          
          const sectionEl = sectionRefs.current[String(category.id)];
          if (sectionEl) {
            const rect = sectionEl.getBoundingClientRect();

            // Calculate visible area within the scroll container
            const visibleTop = Math.max(rect.top, containerTop);
            const visibleBottom = Math.min(rect.bottom, containerTop + containerHeight);
            const visibleArea = Math.max(0, visibleBottom - visibleTop);

            if (visibleArea > maxVisibleArea) {
              maxVisibleArea = visibleArea;
              mostVisibleCategory = String(category.id);
            }
          }
        });

        // Update selected category if different
        if (mostVisibleCategory && mostVisibleCategory !== selectedCategory) {
          setSelectedCategory(mostVisibleCategory);
        }
      }, 100); // 100ms debounce
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };
  }, [filteredCategories, selectedCategory]);

  // Handle keyboard navigation for category chips
  const handleCategoryChipKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number, totalChips: number) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        const nextIndex = (index + 1) % totalChips;
        categoryChipRefs.current[nextIndex]?.focus();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const prevIndex = (index - 1 + totalChips) % totalChips;
        categoryChipRefs.current[prevIndex]?.focus();
      } else if (e.key === "Home") {
        e.preventDefault();
        categoryChipRefs.current[0]?.focus();
      } else if (e.key === "End") {
        e.preventDefault();
        categoryChipRefs.current[totalChips - 1]?.focus();
      }
    },
    [],
  );

  const announce = useCallback((next: SelectionKey[], what: string, sectionName: string, removed: boolean) => {
    const filterWord = next.length === 1 ? "filter" : "filters";
    setLiveMessage(
      `${what} in ${sectionName} ${removed ? "removed" : "selected"}. ${next.length} ${filterWord} active.`,
    );
  }, []);

  // Toggle one checkbox: "this tag in this section". A complex section's tick is that section only.
  const handleOptionToggle = useCallback(
    (headingId: number, tagId: number, tagName: string, sectionName: string, isComplex = false) => {
      const wasSelected = isInstanceSelected(selectedOptions, headingId, tagId);
      if (isComplex) {
        const next = toggleSectionOnly(selectedOptions, headingId, tagId, visibleSections);
        announce(next, tagName, sectionName, wasSelected);
        onOptionsChange(next);
        return;
      }
      let next = toggleInstance(selectedOptions, headingId, tagId, tagSections, visibleSections);

      if (tagId === 428 && wasSelected) {
        // The alternatives under this step-free instance go with it; the map is built from the
        // pre-untick keys so a legacy section-less alternative survives in the other sections.
        next = tagsToAdd.reduce(
          (keys: SelectionKey[], subTag: any) =>
            isInstanceSelected(keys, headingId, subTag.id)
              ? toggleInstance(keys, headingId, subTag.id, familySections, visibleSections)
              : keys,
          next,
        );
        // Complex sections list the alternatives as ordinary checkboxes, so their ticks stay.
        if (!isTagSelectedOutside(next, 428, complexSectionIds)) {
          next = tagsToAdd.reduce((keys: SelectionKey[], subTag: any) => removeTagExcept(keys, subTag.id, complexSectionIds), next);
        }
      }

      announce(next, tagName, sectionName, wasSelected);
      onOptionsChange(next);
    },
    [selectedOptions, tagSections, visibleSections, complexSectionIds, familySections, tagsToAdd, announce, onOptionsChange],
  );

  const allSubItemsSelectedIn = useCallback(
    (headingId: number) =>
      tagsToAdd.length > 0 &&
      tagsToAdd.every((subTag: any) => isInstanceSelected(selectedOptions, headingId, subTag.id)),
    [selectedOptions, tagsToAdd],
  );

  // "All" alternatives under one step-free instance
  const handleAllSubItemsToggle = useCallback(
    (headingId: number, sectionName: string) => {
      const allSelected = allSubItemsSelectedIn(headingId);
      const next = tagsToAdd.reduce(
        (keys: SelectionKey[], subTag: any) =>
          isInstanceSelected(keys, headingId, subTag.id) === allSelected
            ? toggleInstance(keys, headingId, subTag.id, familySections, visibleSections)
            : keys,
        selectedOptions,
      );
      announce(next, `${i18n.get("all")} step-free alternatives`, sectionName, allSelected);
      onOptionsChange(next);
    },
    [allSubItemsSelectedIn, selectedOptions, tagsToAdd, familySections, visibleSections, announce, onOptionsChange, i18n],
  );

  // One alternative under one step-free instance
  const handleSubItemToggle = useCallback(
    (headingId: number, tagId: number, tagName: string, sectionName: string) => {
      const wasSelected = isInstanceSelected(selectedOptions, headingId, tagId);
      const next = toggleInstance(selectedOptions, headingId, tagId, familySections, visibleSections);
      announce(next, tagName, sectionName, wasSelected);
      onOptionsChange(next);
    },
    [selectedOptions, familySections, visibleSections, announce, onOptionsChange],
  );

  // Apply saved filters
  const applySavedFilters = useCallback(
    (apply: boolean) => {
      setIsSavedFiltersApplied(apply);

      if (savedFilterKeys) {
        // Union / subtraction are section-aware: a legacy "any section" tick is made explicit first.
        onOptionsChange(
          apply
            ? unionSelections(selectedOptions, savedFilterKeys, visibleSections)
            : subtractSelections(selectedOptions, savedFilterKeys, visibleSections),
        );
      }
    },
    [selectedOptions, savedFilterKeys, visibleSections, onOptionsChange, setIsSavedFiltersApplied]
  );

  // Toggle show more/less for a category
  const toggleCategoryExpand = useCallback((categoryKey: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryKey)) {
        next.delete(categoryKey);
      } else {
        next.add(categoryKey);
      }
      return next;
    });
  }, []);

  // Handle clear all
  const handleClearAll = useCallback(() => {
    setLiveMessage("All accessibility filters cleared.");

    setSearchTerm("");
    setSelectedCategory(null);
    onClearAll();
  }, [onClearAll, selectedOptions.length]);

  // Ticked boxes per section, counted over the whole section so the panel's search box can't hide a
  // tick. Step-free alternatives are drawn, and so counted, only under a ticked step-free box.
  const sectionSelectedCounts = useMemo(() => {
    const counts = new Map<number, number>();
    (preparedData as FilterSection[]).forEach((category) => {
      const ticked = category.accessibility_tags.filter((tag) =>
        isInstanceSelected(selectedOptions, category.id, tag.id),
      );
      let count = ticked.length;
      if (category.kind !== "complex" && ticked.some((tag) => tag.id === 428)) {
        count += tagsToAdd.filter((subTag: { id: number }) => isInstanceSelected(selectedOptions, category.id, subTag.id)).length;
      }
      counts.set(category.id, count);
    });
    return counts;
  }, [preparedData, selectedOptions, tagsToAdd]);

  // Handle focus on focusable elements to ensure they scroll into view within the panel only
  const handleFocusableElementFocus = useCallback((e: React.FocusEvent) => {
    if (!contentRef.current) return;
    const element = e.target as HTMLElement;
    // For hidden inputs (like in Checkbox with sr-only), scroll the visible parent
    // Otherwise scroll the element itself
    const elementToScroll = element.classList.contains('sr-only')
      ? element.closest('label') || element.closest('li') || element
      : element;
    if (!elementToScroll) return;

    // Capture reference before the async frame so it stays valid even if the
    // event object is recycled by React's synthetic event pooling.
    const captured = elementToScroll as HTMLElement;

    // Defer rect computation to the next animation frame.
    // Chrome changes #main.scrollTop before firing the scroll event, so if we
    // read rects synchronously here they may reflect the panel being off-screen.
    // By waiting one rAF we guarantee preventScrollJump has already reset
    // #main.scrollTop and all rects are computed against the correct viewport.
    requestAnimationFrame(() => {
      if (!contentRef.current) return;

      // Manually adjust contentRef.scrollTop so scroll is scoped entirely to the
      // panel's internal scroll container and never propagates to #main or body
      const container = contentRef.current;
      const containerRect = container.getBoundingClientRect();
      const elementRect = captured.getBoundingClientRect();

      const isAlreadyVisible =
        elementRect.top >= containerRect.top &&
        elementRect.bottom <= containerRect.bottom;

      if (!isAlreadyVisible) {
        if (elementRect.bottom > containerRect.bottom) {
          container.scrollTop += elementRect.bottom - containerRect.bottom + 8;
        } else if (elementRect.top < containerRect.top) {
          container.scrollTop -= containerRect.top - elementRect.top + 8;
        }
      }
    });
  }, []);

  // Disable parent page scroll when accessibility panel is open
  useEffect(() => {
    const mainElement = document.getElementById('main');

    // Save the current overflow values and scroll position to restore later
    const originalBodyOverflow = document.body.style.overflow;
    const originalMainOverflow = mainElement?.style.overflow;
    const savedScrollTop = mainElement?.scrollTop ?? 0;

    // Disable scroll on body and main container
    document.body.style.overflow = "hidden";
    if (mainElement) {
      mainElement.style.overflow = "hidden";
    }

    // Chrome's native focus-scroll can still change scrollTop on overflow:hidden
    // elements when a checkbox/input is focused via click. Intercept any scroll
    // events and immediately reset scrollTop back to the saved position.
    const preventScrollJump = () => {
      if (mainElement && mainElement.scrollTop !== savedScrollTop) {
        mainElement.scrollTop = savedScrollTop;
      }
    };
    mainElement?.addEventListener('scroll', preventScrollJump);

    // Same jump, but on the window: the panel isn't always inside #main, and
    // body's overflow doesn't stop the document itself from being scrolled.
    const savedWindowScroll = { x: window.scrollX, y: window.scrollY };
    const preventWindowJump = () => {
      if (
        window.scrollX !== savedWindowScroll.x ||
        window.scrollY !== savedWindowScroll.y
      ) {
        window.scrollTo(savedWindowScroll.x, savedWindowScroll.y);
      }
    };
    window.addEventListener('scroll', preventWindowJump);

    // Cleanup: restore scroll when panel closes
    return () => {
      window.removeEventListener('scroll', preventWindowJump);
      mainElement?.removeEventListener('scroll', preventScrollJump);
      document.body.style.overflow = originalBodyOverflow;
      if (mainElement) {
        mainElement.style.overflow = originalMainOverflow || "";
      }
    };
  }, []);

  if (!isOpen) return null;
  if (!accessibilityTags || preparedData.length === 0) return null;

  return (
    <FocusTrap
      focusTrapOptions={{
        initialFocus: false,
        allowOutsideClick: true,
        returnFocusOnDeactivate: true,
        escapeDeactivates: false,
      }}
    >
      {/* The handlers only stop the document-level outside-click closer (mousedown) and the
          parent's click from firing; the dialog is operated by its own controls and the Esc listener. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        ref={panelRef}
        id={id}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        className={cn(
          "bg-bg-brand-secondary-default border border-neutral-100",
          "rounded-xl shadow-lg p-3 w-full md:w-[400px] lg:w-[520px] max-h-[70vh] overflow-hidden flex flex-col",
          className,
        )}
        role="dialog"
        aria-modal="true"
        aria-label={i18n.get("accessibilityFilters")}
      >
        {/* Header */}
        <div className="flex items-center justify-between h-11 px-2 shrink-0">
          <h2 className="title-s-semibold text-fg-neutral-strong">
            {i18n.get("accessibility")}
          </h2>
          {!onApply && (
            <button
              type="button"
              onClick={handleClearAll}
              className="label-m-medium text-fg-brand-subtle hover:text-fg-brand-strong underline underline-offset-2 px-3 py-1 focus-blue"
              aria-label={`${i18n.get("clearAll")} accessibility filters`}
            >
              {i18n.get("clearAll")}
            </button>
          )}
        </div>

        {/* Category Chips */}
        <ScrollContainer className="flex gap-1 py-1 px-0 scrollbar-hide shrink-0 overflow-x-hidden">
        <ul className="flex gap-1 py-1 px-0 scrollbar-hide shrink-0 overflow-x-hidden list-none m-0" aria-label="Accessibility categories">
          {preparedData.map((category: any, index: number) => {
            if (category.accessibility_tags.length <= 0) return null;
            const categoryKey = String(category.id);
            const isSelected = selectedCategory === categoryKey;
            
            // Calculate chip index (excluding categories with no tags)
            const chipIndex = preparedData
              .slice(0, index)
              .filter((cat: any) => cat.accessibility_tags.length > 0)
              .length;
            const totalChips = preparedData.filter((cat: any) => cat.accessibility_tags.length > 0).length;
            
            return (
              <li key={categoryKey} className="flex-shrink-0">
                <button
                  ref={(el) => {
                    if (el) categoryChipRefs.current[chipIndex] = el;
                  }}
                  type="button"
                  aria-pressed={isSelected}
                  aria-controls={filteredCategories.some((c: FilterSection) => c.id === category.id) ? `category-section-${category.id}` : undefined}
                  onClick={() => handleCategoryClick(categoryKey)}
                  onKeyDown={(e) => handleCategoryChipKeyDown(e, chipIndex, totalChips)}
                  className={cn(
                    "flex-shrink-0 min-w-[44px] min-h-[40px] px-3 py-2 rounded-lg whitespace-nowrap transition-colors focus-blue",
                    isSelected
                      ? "label-m-semibold bg-bg-brand-tertiary-default border border-border-token-brand-subtle text-fg-brand-subtle"
                      : "label-m-medium bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover text-fg-neutral-subtle",
                  )}
                >
                  {category.name}
                </button>
              </li>
            );
          })}
        </ul>
        </ScrollContainer>

        <div className={cn("flex-1", isZoomedIn ? "overflow-y-auto" : "flex flex-col overflow-hidden")}>
          {/* Search Input */}
          <div className="mt-3 shrink-0">
            <div
              className={cn(
                "flex items-center gap-4 min-h-[48px] px-4 py-2",
                "bg-bg-brand-secondary-default border border-border-token-neutral-subtle rounded-lg",
                "focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[var(--focus-ring-default)]",
              )}
            >
              <input
                ref={searchInputRef}
                type="text"
                placeholder={i18n.get("searchAccessibilityFeatures")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={cn(
                  "flex-1 min-w-[44px] bg-transparent border-none outline-none focus:ring-0",
                  "text-base md:text-[13px] text-fg-neutral-subtle",
                  "placeholder:text-fg-neutral-subtle",
                )}
                aria-label={i18n.get("searchAccessibilityOptions")}
              />
              <Search
                size={24}
                className="text-fg-neutral-subtle flex-shrink-0"
                aria-hidden="true"
                focusable="false"
              />
            </div>
          </div>

          {/* Saved Filters Toggle */}
          {isLoggedIn && showSavedFilters && (
            <div className="shrink-0 px-2 pt-3 ">
              <div className="flex items-center gap-3 py-3 bg-bg-brand-ghost-default">
                <Switch
                  isOn={isSavedFiltersApplied}
                  onChange={(value) => {
                    applySavedFilters(value);
                  }}
                  aria-label={
                    isSavedFiltersApplied
                      ? `${i18n.get("applySavedAccessibilityFilters")} selected`
                      : `${i18n.get("applySavedAccessibilityFilters")}`
                  }
                />
                <div className="label-m-medium text-fg-neutral-strong">
                  {i18n.get("applySavedAccessibilityFilters")}
                </div>
              </div>
            </div>
          )}

          {/* Options List - All categories shown */}
          <div ref={contentRef} className={cn("mt-3", isZoomedIn ? "" : "flex-1 overflow-y-auto")}>
          <Accordion type="multiple" value={openSections} onValueChange={setOpenSections}>
          {filteredCategories.map((category: any) => {
            if (category.accessibility_tags.length <= 0) return null;

            const categoryKey = String(category.id);
            const isComplex = category.kind === "complex";
            const isExpanded = expandedCategories.has(categoryKey);
            const selectedCount = sectionSelectedCounts.get(category.id) ?? 0;
            const tagsToDisplay = isExpanded
              ? category.accessibility_tags
              : category.accessibility_tags.slice(0, 5);

            return (
              <AccordionItem
                key={categoryKey}
                value={categoryKey}
                ref={(el) => {
                  if (el) sectionRefs.current[categoryKey] = el;
                }}
                id={`category-section-${category.id}`}
                className="mb-4 border-none"
              >
                {/* Section header: heading wrapping the disclosure button (WAI-ARIA accordion pattern).
                    Not the shadcn AccordionTrigger — its Header carries role="none". */}
                <AccordionPrimitive.Header asChild>
                  <h3
                    className={cn(
                      "rounded-lg mb-2 bg-container-brand-minimal",
                      selectedCount > 0 && "border border-border-token-brand-subtle",
                    )}
                  >
                    <AccordionPrimitive.Trigger
                      ref={(el) => {
                        if (el) categoryTriggerRefs.current[categoryKey] = el;
                      }}
                      className="flex w-full min-h-[44px] items-center gap-2 p-3 rounded-lg text-left text-fg-neutral-strong title-s-semibold focus-blue [&[data-state=open]>svg]:rotate-180"
                      onFocus={handleFocusableElementFocus}
                      aria-label={
                        selectedCount > 0
                          ? `${category.name}, ${i18n.t("sectionFiltersApplied", { count: selectedCount })}`
                          : undefined
                      }
                    >
                      <span className="flex-1 min-w-0">{category.name}</span>
                      {selectedCount > 0 && (
                        <span
                          className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium"
                          aria-hidden="true"
                        >
                          {selectedCount}
                        </span>
                      )}
                      <ChevronDown
                        aria-hidden="true"
                        focusable="false"
                        className="h-5 w-5 shrink-0 text-fg-neutral-subtle transition-transform duration-200"
                      />
                    </AccordionPrimitive.Trigger>
                  </h3>
                </AccordionPrimitive.Header>

                <AccordionContent className="pb-0 pt-0">
                {/* Tags */}
                <ul className="px-2 flex flex-col gap-3">
                  {tagsToDisplay.map((tag: any) => (
                    <li key={tag.id}>
                      <Checkbox
                        checked={isInstanceSelected(selectedOptions, category.id, tag.id)}
                        onCheck={() => handleOptionToggle(category.id, tag.id, tag.name, category.name, isComplex)}
                        label={tag.name}
                        ariaLabel={`${tag.name}, ${category.name}`}
                        onFocus={handleFocusableElementFocus}
                      />

                      {/* Step-free alternatives, per ticked step-free instance (basic sections only) */}
                      {!isComplex && tag.id === 428 && tagsToAdd.length > 0 && isInstanceSelected(selectedOptions, category.id, tag.id) && (
                        <div className="mt-3 ml-6" role="group" aria-labelledby={`step-free-alternatives-${category.id}`}>
                          <p id={`step-free-alternatives-${category.id}`} className="mb-2 text-fg-brand-strong label-m-medium">
                            {i18n.get("wouldAnyOfTheseAlsoWork")}
                          </p>
                          <Checkbox
                            className="mb-2"
                            label={i18n.get("all")}
                            ariaLabel={`${i18n.get("all")} step-free alternatives, ${category.name}`}
                            checked={allSubItemsSelectedIn(category.id)}
                            onCheck={() => handleAllSubItemsToggle(category.id, category.name)}
                            onFocus={handleFocusableElementFocus}
                          />
                          <div className="ml-6 flex flex-col gap-2">
                            {tagsToAdd.map((subTag: any) => (
                              <Checkbox
                                key={subTag.id}
                                label={subTag.name}
                                ariaLabel={`${subTag.name}, ${category.name}`}
                                checked={isInstanceSelected(selectedOptions, category.id, subTag.id)}
                                onCheck={() => handleSubItemToggle(category.id, subTag.id, subTag.name, category.name)}
                                onFocus={handleFocusableElementFocus}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>

                {/* Show More/Less Button */}
                {category.accessibility_tags.length > 5 && (
                  <StandardButton
                    onClick={() => toggleCategoryExpand(categoryKey)}
                    className="ml-2 mt-2"
                    variant="blueLink"
                    size="sm"
                    onFocus={handleFocusableElementFocus}
                  >
                    {isExpanded ? i18n.get("showLess") : i18n.get("showMore")}
                  </StandardButton>
                )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
          </Accordion>

          {/* No results message */}
          {filteredCategories.length === 0 && searchTerm && (
            <p className="text-center py-8 label-m-medium text-fg-neutral-subtle">
              {i18n.get("noResultsFound")}
            </p>
          )}
          </div>
        </div>

        {onApply && (
          <div className="flex gap-3 pt-3 mt-3 shrink-0 border-t border-border-token-neutral-minimal">
            <StandardButton
              type="button"
              variant="secondary"
              size="lg"
              className="flex-1"
              onClick={handleClearAll}
              aria-label={i18n.get("clearAllAccessibilityFilters")}
            >
              {i18n.get("clearAll")}
            </StandardButton>
            <StandardButton
              type="button"
              variant="primaryNew"
              size="lg"
              className="flex-1 hover:bg-bg-brand-primary-hover focus-blue"
              onClick={onApply}
            >
              {i18n.get("applyFilters")}
            </StandardButton>
          </div>
        )}

        {/* Visually hidden live region for screen reader announcements */}
        <div
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {liveMessage}
        </div>
      </div>
    </FocusTrap>
  );
}

export default AccessibilityFilterPanel;
